// ---------------------------------------------------------------------------
// risk-utils.ts — Risk scoring, banding, and comparison logic shared by the
// report model builder and the intelligence panel.
// ---------------------------------------------------------------------------

import type { RiskBand, RiskLevel } from './types';

export interface RiskLike {
  likelihood?: string;
  impact?: string;
}

export function normalizeRiskLevel(value: string | undefined): RiskLevel {
  if (value === 'high' || value === 'medium' || value === 'low') return value;
  return 'medium';
}

export function riskLevelWeight(level: string | undefined): number {
  if (level === 'high') return 3;
  if (level === 'medium') return 2;
  return 1;
}

export function deriveRiskBand(risk: RiskLike | null): RiskBand {
  const likelihood = normalizeRiskLevel((risk && risk.likelihood) as string);
  const impact = normalizeRiskLevel((risk && risk.impact) as string);

  if (likelihood === 'high' && impact === 'high') return 'high';
  if (
    (likelihood === 'medium' && impact === 'high')
    || (likelihood === 'high' && impact === 'medium')
  ) {
    return 'medium-high';
  }
  if (likelihood === 'medium' && impact === 'medium') return 'medium';
  if (
    (likelihood === 'medium' && impact === 'low')
    || (likelihood === 'low' && impact === 'medium')
  ) {
    return 'medium-low';
  }
  return 'low';
}

export function riskBandRank(value: RiskBand | string): number {
  switch (value) {
    case 'high': return 5;
    case 'medium-high': return 4;
    case 'medium': return 3;
    case 'medium-low': return 2;
    case 'low': return 1;
    default: return 0;
  }
}

/**
 * Compare two risks by band → impact → likelihood. Returns negative if
 * `left` is higher priority than `right`. Ties return 0 — callers can
 * add their own tiebreaker (e.g. originalIndex).
 */
export function compareRisks(left: RiskLike, right: RiskLike): number {
  const bandDelta = riskBandRank(deriveRiskBand(right)) - riskBandRank(deriveRiskBand(left));
  if (bandDelta !== 0) return bandDelta;

  const impactDelta = riskLevelWeight(right.impact) - riskLevelWeight(left.impact);
  if (impactDelta !== 0) return impactDelta;

  const likelihoodDelta = riskLevelWeight(right.likelihood) - riskLevelWeight(left.likelihood);
  if (likelihoodDelta !== 0) return likelihoodDelta;

  return 0;
}
